import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { EvertimeClientRevenueTrendPoint } from '../../services/apis/dashboard/dashboard.service';
import { ThemeService } from '../../services/theme.service';
import { chartColors } from './chart-theme';

@Component({
  selector: 'app-client-revenue-history-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  host: { class: 'block w-full h-full' },
  templateUrl: './client-revenue-history-chart.component.html',
})
export class ClientRevenueHistoryChartComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @Input() data: EvertimeClientRevenueTrendPoint[] = [];

  options: EChartsOption | null = null;

  @ViewChild('chartContainer', { static: true }) private chartContainer?: ElementRef<HTMLElement>;

  private hasBuiltOptions = false;
  private themeSub?: Subscription;
  private chartInstance?: { resize: () => void };
  private resizeObserver?: ResizeObserver;
  private readonly themeService = inject(ThemeService);

  ngOnInit(): void {
    if (!this.hasBuiltOptions) this.buildOptions();
    this.themeSub = this.themeService.isDark$.pipe(skip(1)).subscribe(() => this.buildOptions());
  }

  ngOnChanges(): void {
    this.buildOptions();
  }

  ngAfterViewInit(): void {
    if (!this.chartContainer?.nativeElement) return;
    this.resizeObserver = new ResizeObserver(() => this.chartInstance?.resize());
    this.resizeObserver.observe(this.chartContainer.nativeElement);
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  onChartInit(instance: { resize: () => void }): void {
    this.chartInstance = instance;
    this.chartInstance.resize();
  }

  private buildOptions(): void {
    this.hasBuiltOptions = true;
    const rows = [...(this.data ?? [])].sort((a, b) => a.month.localeCompare(b.month));
    if (rows.length === 0) {
      this.options = null;
      return;
    }

    const c = chartColors();
    const color = c.primary || c.accent;
    const currency = rows.find(row => row.currencyCode)?.currencyCode ?? 'GBP';
    const formatCurrency = (value: number) => new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);

    this.options = {
      grid: { left: 44, right: 18, top: 18, bottom: 42, containLabel: true },
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: { type: 'line', lineStyle: { color: c.cardBorder, type: 'dashed' } },
        backgroundColor: c.chartSurface,
        borderColor: c.cardBorder,
        borderWidth: 1,
        padding: [10, 12],
        textStyle: { color: c.textPrimary, fontSize: 12 },
        extraCssText: 'box-shadow: 0 12px 28px rgba(0,0,0,0.16); border-radius: 8px;',
        valueFormatter: value => formatCurrency(Number(value)),
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: rows.map(row => row.monthLabel || row.month),
        axisLine: { lineStyle: { color: c.cardBorder } },
        axisTick: { show: false },
        axisLabel: { color: c.textSecondary, fontSize: 11, margin: 12 },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: c.textSecondary,
          fontSize: 11,
          formatter: (value: number) => {
            if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}m`;
            if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(0)}k`;
            return `${value}`;
          },
        },
        splitLine: { lineStyle: { color: c.cardBorder, type: 'dashed', opacity: 0.75 } },
      },
      series: [
        {
          name: 'Revenue',
          type: 'line',
          data: rows.map(row => row.revenue),
          smooth: 0.22,
          symbol: 'circle',
          symbolSize: rows.length <= 4 ? 9 : 6,
          itemStyle: { color },
          lineStyle: { color, width: 2.8 },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: this.toRgba(color, 0.24) },
                { offset: 1, color: this.toRgba(color, 0) },
              ],
            },
          },
        },
      ],
    };
  }

  private toRgba(color: string, alpha: number): string {
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color.trim());
    if (!match) return color;
    const r = parseInt(match[1], 16);
    const g = parseInt(match[2], 16);
    const b = parseInt(match[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}
